import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';
import Utility from '../../../../GlobalModules/Utility/Utility';

const BUSINESS_REGISTER_BASE_URL = `${process.env.EXTENDED_URL}/business_register`;

/**
 * Check if user has an active business register
 * @returns {Promise<Object|null>} The active register or null if none exists
 */
export const getActiveBusinessRegister = async () => {
  try {
    const userId = localStorage.getItem('userId');
    const storeId = localStorage.getItem('store_id');
    // console.log("userId====",userId)
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/current`, {
      user_id: userId,
      store_id: storeId
    });

    if (response?.data?.success) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching active business register:', error);
    return null;
  }
};

/**
 * Open a new business register
 * @param {Object} data - Register data including billing_counter_id and opening_amount
 * @returns {Promise<Object|null>} The opened register or null if failed
 */
export const openBusinessRegister = async (data) => {
  try {
    const response = await post(`${BUSINESS_REGISTER_BASE_URL}/open`, data);
    
    if (response?.data?.success) {
      Utility.toastNotifications('Business register opened successfully', 'success');
      return response.data.data;
    } else {
      Utility.toastNotifications(response.data.message || 'Failed to open business register', 'error');
      return null;
    }
  } catch (error) {
    console.error('Error opening business register:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to open business register', 'error');
    return null;
  }
};

/**
 * Close a business register
 * @param {Object} data - Closing data including closing_amount, credit_card_slips, cheques
 * @returns {Promise<{success: boolean, data?: object, message?: string}>} 
 */
export const closeBusinessRegister = async (data) => {
  try {
    const apiData = {
      closing_amount: data.closing_amount,
      credit_card_slips: data.credit_card_slips || 0,
      cheques: data.cheques || 0,
      store_id: data.store_id,
      expected_closing_amount: data.expected_closing_amount || 0
    };
    const response = await post(`${BUSINESS_REGISTER_BASE_URL}/close`, apiData);
    
    if (response?.data?.success) {
      Utility.toastNotifications('Business register closed successfully', 'success');
    } else {
      Utility.toastNotifications(response?.data?.message || 'Failed to close business register', 'error');
    }

    return {
      success: response?.data?.success || false,
      message: response?.data?.message || '',
      data: response?.data?.data || null
    };
  } catch (error) {
    console.error('Error closing business register:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to close business register', 'error');

    return {
      success: false,
      message: error.response?.data?.message || 'Failed to close business register',
      data: null
    };
  }
};

/**
 * Get list of business registers
 * @param {Object} filters - Filter options including limit, offset, sort, search
 * @returns {Promise<Object>} The list of registers and metadata
 */
export const getBusinessRegisterList = async (filters = {}) => {
  try {
    // Format filters according to the API structure
    let queryParams = {};
    
    if (filters) {
      // Handle search term
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        queryParams.filters = JSON.stringify({
          limit: filters.limit || 10,
          offset: filters.offset || 0,
          search_attr: filters.search,
          store_id: localStorage.getItem('store_id') || localStorage.getItem('storeId')
        });
      } else {
        // Handle pagination and sorting
        const filtersObj = {
          limit: filters.limit || 10,
          offset: filters.offset || 0,
          store_id: localStorage.getItem('store_id') || localStorage.getItem('storeId')
        };
        
        // Add sorting if provided
        if (filters.sort && filters.sort.field) {
          filtersObj.sort = [{
            colId: filters.sort.field,
            sort: filters.sort.order
          }];
        }
        
        queryParams.filters = JSON.stringify(filtersObj);
      }
    }
    
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}`, queryParams);
    
    if (response?.data?.success) {
      return {
        data: response.data.data,
        total: response.data.total || response.data.data.length,
        success: true
      };
    }
    
    Utility.toastNotifications(response?.data?.message || 'Failed to fetch business registers', 'error');
    return { data: [], total: 0, success: false };
  } catch (error) {
    console.error('Error fetching business registers:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to fetch business registers', 'error');
    return { data: [], total: 0, success: false };
  }
};

/**
 * Get details of a specific business register
 * @param {string} slack - The slack identifier of the register
 * @returns {Promise<Object|null>} The register details or null if not found
 */
export const getBusinessRegister = async (slack) => {
  try {
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/${slack}`);
    
    if (response?.data?.success) {
      return response.data.data;
    }
    
    Utility.toastNotifications(response?.data?.message || 'Failed to fetch business register details', 'error');
    return null;
  } catch (error) {
    console.error('Error fetching business register details:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to fetch business register details', 'error');
    return null;
  }
};

/**
 * Delete a business register
 * @param {string} slack - The slack identifier of the register to delete
 * @returns {Promise<Object>} Response with success/failure status
 */
export const deleteBusinessRegister = async (slack) => {
  try {
    const response = await del(`${BUSINESS_REGISTER_BASE_URL}/${slack}`);
    
    if (response?.data?.success) {
      Utility.toastNotifications('Business register deleted successfully', 'success');
      return { success: true };
    }
    
    Utility.toastNotifications(response?.data?.message || 'Failed to delete business register', 'error');
    return { success: false };
  } catch (error) {
    console.error('Error deleting business register:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to delete business register', 'error');
    return { success: false };
  }
};

/**
 * Export business registers
 * @param {Object} filters - Filter options for the export
 * @returns {Promise<Object>} Response with download link
 */
export const exportBusinessRegisters = async (filters = {}) => {
  try {
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/export`, filters);
    
    if (response?.data?.success) {
      return {
        success: true,
        link: response.data.data?.download_link || ''
      };
    }
    
    Utility.toastNotifications(response?.data?.message || 'Failed to export business registers', 'error');
    return { success: false };
  } catch (error) {
    console.error('Error exporting business registers:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to export business registers', 'error');
    return { success: false };
  }
};

/**
 * Get billing counters for a store
 * @param {number} storeId - The store ID to get billing counters for
 * @returns {Promise<Array>} List of billing counters
 */
export const getBillingCounters = async (storeId) => {
  try {
    // If no store ID is provided, try to get it from local storage
    const store_id = storeId || localStorage.getItem('storeId') || localStorage.getItem('store_id');
    
    if (!store_id) {
      console.error('No store ID available for fetching billing counters');
      return [];
    }
    
    // Use the provided endpoint format for billing counters
    const response = await get(`${process.env.EXTENDED_URL}/billing_counter`, {
      filters: JSON.stringify({ store_id: parseInt(store_id) })
    });
    
    if (response?.data?.success) {
      return response.data.data || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching billing counters:', error);
    return [];
  }
};

/**
 * Fetch the current register amount.
 * @param {number} storeId - Target store ID
 * @returns {Promise<{success: boolean, amount: number, message?: string}>}
 */
export const getRegisterOrderAmount = async (storeId) => {
  try {
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/get_register_order_amount`, { store_id: storeId }, "global");

    if (response?.data?.success) return { success: true, amount: response.data.data };

    return { success: false, amount: 0, message: response?.data?.message };
  } catch (error) {
    console.error('Error fetching register amount:', error);
    return { success: false, amount: 0, message: 'Error fetching register amount' };
  }
};

/**
 * Generate PDF report for a business register
 * @param {string} slack - The slack identifier of the register
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export const generateRegisterPDF = async (slack) => {
  try {
    const storeId = localStorage.getItem('store_id') || localStorage.getItem('storeId');
    const queryParams = storeId ? { store_id: storeId } : {};
    
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/${slack}/pdf-data`, queryParams);
    
    if (response?.data?.success) {
      return {
        success: true,
        data: response.data.data
      };
    }
    
    Utility.toastNotifications(response?.data?.message || 'Failed to get register data', 'error');
    return { success: false, message: response?.data?.message };
  } catch (error) {
    console.error('Error getting register data:', error);
    Utility.toastNotifications(error.response?.data?.message || 'Failed to get register data', 'error');
    return { success: false, message: error.response?.data?.message || 'Failed to get register data' };
  }
};

/**
 * Fetch all open business registers for a store and business day
 * @param {string|number} storeId - The store ID
 * @param {string|number} businessDayId - The business day ID
 * @returns {Promise<{success: boolean, hasOpenRegisters: boolean, openRegisters: Array, message?: string}>}
 */
export const fetchAllOpenRegisters = async (storeId, businessDayId) => {
  try {
    const response = await get(`${BUSINESS_REGISTER_BASE_URL}/open_registers`, {
      store_id: storeId,
      business_day_id: businessDayId
    });
    if (response?.data?.success) {
      return {
        success: true,
        hasOpenRegisters: (response?.data?.data || []).length > 0,
        openRegisters: response?.data?.data || [],
        message: response?.data?.message || ''
      };
    }

    return {
      success: false,
      hasOpenRegisters: false,
      openRegisters: [],
      message: response?.data?.message || 'Failed to fetch open registers'
    };
  } catch (error) {
    console.error('Error fetching open registers:', error);
    return {
      success: false,
      hasOpenRegisters: false,
      openRegisters: [],
      message: error.response?.data?.message || 'Failed to fetch open registers'
    };
  }
};

/**
 * Close all open business registers for a store and business day
 * @param {Object} payload - The close payload
 * @param {string|number} payload.store_id - The store ID
 * @param {string|number} payload.business_day_id - The business day ID
 * @param {Array} payload.registers - Array of register close data
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const closeAllBusinessRegisters = async (payload) => {
  try {
    const response = await post(`${BUSINESS_REGISTER_BASE_URL}/close_all`, payload);
    if (response?.data?.success) {
      return {
        success: true,
        data: response?.data?.data || {},
        message: response?.data?.message || 'Registers closed successfully'
      };
    }
    return {
      success: false,
      message: response?.data?.message || 'Failed to close registers'
    };
  } catch (error) {
    console.error('Error closing all business registers:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to close registers'
    };
  }
};

export default {
  getActiveBusinessRegister,
  getBusinessRegisterList,
  getBusinessRegister,
  openBusinessRegister,
  closeBusinessRegister,
  deleteBusinessRegister,
  exportBusinessRegisters,
  getBillingCounters,
  getRegisterOrderAmount,
  generateRegisterPDF,
  fetchAllOpenRegisters,
  closeAllBusinessRegisters
};
